import React, { FC, useState, useRef } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, Button, IconButton, Chip,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, LinearProgress, Alert, Tooltip, Stack, Divider, Menu, MenuItem
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import TableChartIcon from '@mui/icons-material/TableChart';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import moment from 'moment';
import { toast } from 'react-toastify';
import { useAppDispatch } from '../../store/hooks';
import { createHoliday } from '../../store/hrmsStore/hrmsThunk';

export interface ParsedHolidayItem {
    id: number;
    name: string;
    date: string;
    holidayType: 'Mandatory' | 'Optional' | 'Default';
    state: string;
    country: string;
    locationCode?: string;
    locationInfo?: string;
    isValid: boolean;
    errorReason?: string;
}

export const SAMPLE_HOLIDAYS_TEMPLATE = [
    { "Date (YYYY-MM-DD)": "2028-01-01", "Name": "New Year's Day", "Type (Mandatory/Optional)": "Optional", "State": "All", "Country": "India" },
    { "Date (YYYY-MM-DD)": "2028-01-15", "Name": "Makar Sankranti", "Type (Mandatory/Optional)": "Optional", "State": "All", "Country": "India" },
    { "Date (YYYY-MM-DD)": "2028-01-15", "Name": "Pongal", "Type (Mandatory/Optional)": "Optional", "State": "All", "Country": "India" },
    { "Date (YYYY-MM-DD)": "2028-01-26", "Name": "Republic Day", "Type (Mandatory/Optional)": "Mandatory", "State": "All", "Country": "India" },
    { "Date (YYYY-MM-DD)": "2028-01-31", "Name": "Vasant Panchami", "Type (Mandatory/Optional)": "Optional", "State": "All", "Country": "India" },
    { "Date (YYYY-MM-DD)": "2028-02-19", "Name": "Shivaji Jayanti", "Type (Mandatory/Optional)": "Optional", "State": "All", "Country": "India" },
    { "Date (YYYY-MM-DD)": "2028-02-23", "Name": "Maha Shivaratri", "Type (Mandatory/Optional)": "Mandatory", "State": "All", "Country": "India" },
    { "Date (YYYY-MM-DD)": "2028-02-27", "Name": "Ramzan Id (Tentative Date)", "Type (Mandatory/Optional)": "Mandatory", "State": "All", "Country": "India" },
    { "Date (YYYY-MM-DD)": "2028-03-11", "Name": "Holi", "Type (Mandatory/Optional)": "Mandatory", "State": "All", "Country": "India" },
    { "Date (YYYY-MM-DD)": "2028-03-27", "Name": "Ugadi", "Type (Mandatory/Optional)": "Optional", "State": "All", "Country": "India" },
    { "Date (YYYY-MM-DD)": "2028-03-27", "Name": "Gudi Padwa", "Type (Mandatory/Optional)": "Optional", "State": "All", "Country": "India" },
    { "Date (YYYY-MM-DD)": "2028-04-03", "Name": "Rama Navami", "Type (Mandatory/Optional)": "Mandatory", "State": "All", "Country": "India" },
    { "Date (YYYY-MM-DD)": "2028-04-14", "Name": "Good Friday", "Type (Mandatory/Optional)": "Mandatory", "State": "All", "Country": "India" },
    { "Date (YYYY-MM-DD)": "2028-04-16", "Name": "Easter Day", "Type (Mandatory/Optional)": "Optional", "State": "All", "Country": "India" },
    { "Date (YYYY-MM-DD)": "2028-05-06", "Name": "Bakrid (Tentative Date)", "Type (Mandatory/Optional)": "Mandatory", "State": "All", "Country": "India" },
    { "Date (YYYY-MM-DD)": "2028-06-04", "Name": "Muharram/Ashura (Tentative Date)", "Type (Mandatory/Optional)": "Mandatory", "State": "All", "Country": "India" },
    { "Date (YYYY-MM-DD)": "2028-06-24", "Name": "Rath Yatra", "Type (Mandatory/Optional)": "Optional", "State": "All", "Country": "India" },
    { "Date (YYYY-MM-DD)": "2028-08-05", "Name": "Raksha Bandhan", "Type (Mandatory/Optional)": "Optional", "State": "All", "Country": "India" },
    { "Date (YYYY-MM-DD)": "2028-08-13", "Name": "Janmashtami", "Type (Mandatory/Optional)": "Mandatory", "State": "All", "Country": "India" },
    { "Date (YYYY-MM-DD)": "2028-08-15", "Name": "Independence Day", "Type (Mandatory/Optional)": "Mandatory", "State": "All", "Country": "India" },
    { "Date (YYYY-MM-DD)": "2028-08-23", "Name": "Ganesh Chaturthi", "Type (Mandatory/Optional)": "Optional", "State": "All", "Country": "India" },
    { "Date (YYYY-MM-DD)": "2028-09-01", "Name": "Onam", "Type (Mandatory/Optional)": "Optional", "State": "All", "Country": "India" },
    { "Date (YYYY-MM-DD)": "2028-09-27", "Name": "Dussehra", "Type (Mandatory/Optional)": "Mandatory", "State": "All", "Country": "India" },
    { "Date (YYYY-MM-DD)": "2028-10-02", "Name": "Mahatma Gandhi Jayanti", "Type (Mandatory/Optional)": "Mandatory", "State": "All", "Country": "India" },
    { "Date (YYYY-MM-DD)": "2028-10-07", "Name": "Karaka Chaturthi", "Type (Mandatory/Optional)": "Optional", "State": "All", "Country": "India" },
    { "Date (YYYY-MM-DD)": "2028-10-17", "Name": "Diwali/Deepavali", "Type (Mandatory/Optional)": "Mandatory", "State": "All", "Country": "India" },
    { "Date (YYYY-MM-DD)": "2028-10-19", "Name": "Bhai Duj", "Type (Mandatory/Optional)": "Optional", "State": "All", "Country": "India" },
    { "Date (YYYY-MM-DD)": "2028-10-23", "Name": "Chhat Puja", "Type (Mandatory/Optional)": "Optional", "State": "All", "Country": "India" },
    { "Date (YYYY-MM-DD)": "2028-11-24", "Name": "Guru Tegh Bahadur's Martyrdom Day", "Type (Mandatory/Optional)": "Optional", "State": "All", "Country": "India" },
    { "Date (YYYY-MM-DD)": "2028-12-25", "Name": "Christmas", "Type (Mandatory/Optional)": "Mandatory", "State": "All", "Country": "India" }
];

export const downloadHolidayExcelTemplate = () => {
    try {
        const worksheet = XLSX.utils.json_to_sheet(SAMPLE_HOLIDAYS_TEMPLATE);
        worksheet['!cols'] = [
            { wch: 20 },
            { wch: 35 },
            { wch: 25 },
            { wch: 15 },
            { wch: 15 }
        ];
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Holidays');
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, 'Holiday_Template.xlsx');
        toast.success('Excel template downloaded successfully');
    } catch {
        toast.error('Failed to download Excel template');
    }
};

export const downloadHolidayCsvTemplate = () => {
    try {
        const worksheet = XLSX.utils.json_to_sheet(SAMPLE_HOLIDAYS_TEMPLATE);
        const csv = XLSX.utils.sheet_to_csv(worksheet);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        saveAs(blob, 'Holiday_Template.csv');
        toast.success('CSV template downloaded successfully');
    } catch {
        toast.error('Failed to download CSV template');
    }
};

export const exportHolidaysToExcel = (holidays: any[]) => {
    try {
        const exportData = holidays.map(h => ({
            'Date (YYYY-MM-DD)': moment(h.date).format('YYYY-MM-DD'),
            'Name': h.name || '',
            'Type (Mandatory/Optional)': h.holidayType || 'Mandatory',
            'State': h.state || 'All',
            'Country': h.country || 'India',
            'Location Code': h.locationCode || '',
            'Location Info': h.locationInfo || ''
        }));
        const worksheet = XLSX.utils.json_to_sheet(exportData.length > 0 ? exportData : SAMPLE_HOLIDAYS_TEMPLATE);
        worksheet['!cols'] = [
            { wch: 20 },
            { wch: 35 },
            { wch: 25 },
            { wch: 15 },
            { wch: 15 },
            { wch: 15 },
            { wch: 20 }
        ];
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Holidays');
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, `Holidays_Export_${moment().format('YYYYMMDD')}.xlsx`);
        toast.success('Holidays exported to Excel successfully');
    } catch {
        toast.error('Failed to export holidays');
    }
};

interface HolidayImportDialogProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    /** Already-saved holidays — rows with the same date + state + location are skipped as duplicates */
    existingHolidays?: any[];
}

// Same calendar date + same state + same location = duplicate
const normKey = (v: any) => (v && String(v).trim() && String(v).trim().toLowerCase() !== 'all') ? String(v).trim().toLowerCase() : '';
const holidayKey = (h: any) => `${moment(h.date).format('YYYY-MM-DD')}|${normKey(h.state)}|${normKey(h.locationCode)}`;

export const HolidayImportDialog: FC<HolidayImportDialogProps> = ({ open, onClose, onSuccess, existingHolidays = [] }) => {
    const dispatch = useAppDispatch();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [parsedItems, setParsedItems] = useState<ParsedHolidayItem[]>([]);
    const [isParsing, setIsParsing] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const [importProgress, setImportProgress] = useState(0);
    const [templateAnchorEl, setTemplateAnchorEl] = useState<null | HTMLElement>(null);
    const [isDragging, setIsDragging] = useState(false);

    const resetState = () => {
        setSelectedFile(null);
        setParsedItems([]);
        setIsParsing(false);
        setIsImporting(false);
        setImportProgress(0);
        setIsDragging(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleCloseDialog = () => {
        if (isImporting) return;
        resetState();
        onClose();
    };

    const parseDateValue = (val: any): string | null => {
        if (val === null || val === undefined || val === '') return null;
        if (val instanceof Date && !isNaN(val.getTime())) {
            return moment(val).format('YYYY-MM-DD');
        }
        if (typeof val === 'number') {
            // Excel serial date format (approx days since 1899-12-30)
            const jsDate = new Date(Math.round((val - 25569) * 86400 * 1000));
            if (!isNaN(jsDate.getTime())) {
                return moment(jsDate).format('YYYY-MM-DD');
            }
        }
        const str = String(val).trim();
        if (!str) return null;

        const formats = [
            'YYYY-MM-DD', 'YYYY/MM/DD',
            'DD-MM-YYYY', 'DD/MM/YYYY',
            'MM/DD/YYYY', 'MM-DD-YYYY',
            'D-M-YYYY', 'D/M/YYYY',
            'DD-MMM-YYYY', 'DD MMM YYYY',
            'YYYYMMDD'
        ];
        const m = moment(str, formats, true);
        if (m.isValid()) {
            return m.format('YYYY-MM-DD');
        }
        const mLoose = moment(str);
        if (mLoose.isValid() && mLoose.year() > 1990 && mLoose.year() < 2100) {
            return mLoose.format('YYYY-MM-DD');
        }
        return null;
    };

    const normalizeType = (val: any): 'Mandatory' | 'Optional' | 'Default' => {
        const str = String(val || '').trim().toLowerCase();
        if (str.includes('mandat') || str.includes('public') || str.includes('national')) {
            return 'Mandatory';
        }
        if (str.includes('opt') || str.includes('restrict') || str.includes('float')) {
            return 'Optional';
        }
        if (str.includes('def')) {
            return 'Default';
        }
        return 'Mandatory';
    };

    const normalizeRow = (rawRow: any, index: number): ParsedHolidayItem => {
        // Find keys case-insensitively
        let nameVal = '';
        let dateVal: any = '';
        let typeVal = '';
        let stateVal = '';
        let countryVal = '';
        let locationCodeVal = '';
        let locationInfoVal = '';

        for (const [k, v] of Object.entries(rawRow)) {
            const cleanKey = k.toLowerCase().replace(/[^a-z0-9]/g, '');
            if (cleanKey.includes('name') || cleanKey.includes('title') || cleanKey === 'holiday') {
                nameVal = String(v || '').trim();
            } else if (cleanKey.includes('date')) {
                dateVal = v;
            } else if (cleanKey.includes('type')) {
                typeVal = String(v || '').trim();
            } else if (cleanKey.includes('state') || cleanKey.includes('region')) {
                stateVal = String(v || '').trim();
            } else if (cleanKey.includes('country')) {
                countryVal = String(v || '').trim();
            } else if (cleanKey.includes('locationcode') || cleanKey.includes('code')) {
                locationCodeVal = String(v || '').trim();
            } else if (cleanKey.includes('locationinfo') || cleanKey.includes('info') || cleanKey.includes('desc')) {
                locationInfoVal = String(v || '').trim();
            }
        }

        const parsedDate = parseDateValue(dateVal);
        let isValid = true;
        let errorReason = '';

        if (!nameVal) {
            isValid = false;
            errorReason = 'Holiday Name is required';
        } else if (!parsedDate) {
            isValid = false;
            errorReason = 'Invalid Date format (Use YYYY-MM-DD)';
        }

        return {
            id: index + 1,
            name: nameVal,
            date: parsedDate || String(dateVal || ''),
            holidayType: normalizeType(typeVal),
            state: stateVal || 'All',
            country: countryVal || 'India',
            locationCode: locationCodeVal,
            locationInfo: locationInfoVal,
            isValid,
            errorReason
        };
    };

    const processFile = async (file: File) => {
        setSelectedFile(file);
        setIsParsing(true);
        try {
            const fileName = file.name.toLowerCase();
            let parsedList: ParsedHolidayItem[] = [];

            if (fileName.endsWith('.json')) {
                const text = await file.text();
                const json = JSON.parse(text);
                const rawArr = Array.isArray(json) ? json : (json.holidays || json.data || []);
                if (!Array.isArray(rawArr) || rawArr.length === 0) {
                    toast.error('Invalid JSON: No holiday list found');
                    setIsParsing(false);
                    return;
                }
                parsedList = rawArr.map((row: any, i: number) => normalizeRow(row, i));
            } else {
                // Excel (.xlsx, .xls) or CSV (.csv)
                const arrayBuffer = await file.arrayBuffer();
                const workbook = XLSX.read(arrayBuffer, { type: 'array', cellDates: true });
                const firstSheet = workbook.SheetNames[0];
                if (!firstSheet) {
                    toast.error('Empty workbook file');
                    setIsParsing(false);
                    return;
                }
                const worksheet = workbook.Sheets[firstSheet];
                const rawRows: any[] = XLSX.utils.sheet_to_json(worksheet, { defval: '', raw: false });
                if (rawRows.length === 0) {
                    toast.error('The selected file contains no rows');
                    setIsParsing(false);
                    return;
                }
                parsedList = rawRows.map((row: any, i: number) => normalizeRow(row, i));
            }

            setParsedItems(parsedList);
        } catch (err: any) {
            toast.error(err?.message || 'Failed to read file. Please ensure it is a valid Excel, CSV or JSON file.');
            setSelectedFile(null);
            setParsedItems([]);
        } finally {
            setIsParsing(false);
        }
    };

    const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            processFile(file);
        }
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files?.[0];
        if (file) {
            processFile(file);
        }
    };

    const handleImport = async () => {
        const validItems = parsedItems.filter(item => item.isValid);
        if (validItems.length === 0) {
            toast.error('No valid holiday records to import');
            return;
        }

        setIsImporting(true);
        setImportProgress(0);
        let successCount = 0;
        let failCount = 0;
        let skippedCount = 0;
        // Skip rows that already exist (or repeat within the file)
        const seen = new Set<string>(existingHolidays.map(holidayKey));

        for (let i = 0; i < validItems.length; i++) {
            const item = validItems[i];
            const key = holidayKey(item);
            if (seen.has(key)) {
                skippedCount++;
                setImportProgress(Math.round(((i + 1) / validItems.length) * 100));
                continue;
            }
            seen.add(key);
            try {
                await dispatch(createHoliday({
                    name: item.name,
                    date: item.date,
                    holidayType: item.holidayType,
                    state: item.state,
                    country: item.country,
                    locationCode: item.locationCode || '',
                    locationInfo: item.locationInfo || '',
                    isActive: true
                })).unwrap();
                successCount++;
            } catch {
                failCount++;
            }
            setImportProgress(Math.round(((i + 1) / validItems.length) * 100));
        }

        setIsImporting(false);
        const extras = [
            skippedCount > 0 ? `${skippedCount} skipped (already exist)` : '',
            failCount > 0 ? `${failCount} failed` : ''
        ].filter(Boolean).join(', ');
        if (successCount > 0) {
            toast.success(`Successfully imported ${successCount} holidays${extras ? ` (${extras})` : ''}`);
            onSuccess();
            handleCloseDialog();
        } else if (skippedCount > 0 && failCount === 0) {
            toast.info(`Nothing imported — all ${skippedCount} holidays already exist`);
            handleCloseDialog();
        } else {
            toast.error(`Failed to import holidays${extras ? ` (${extras})` : ''}`);
        }
    };

    const validCount = parsedItems.filter(i => i.isValid).length;
    const invalidCount = parsedItems.filter(i => !i.isValid).length;

    return (
        <Dialog open={open} onClose={handleCloseDialog} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TableChartIcon sx={{ color: '#488dff' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.15rem' }}>
                        Import Holidays (Excel / CSV / JSON)
                    </Typography>
                </Box>
                <IconButton onClick={handleCloseDialog} disabled={isImporting} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ p: 2.5 }}>
                {/* Template Download Banner */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    backgroundColor: '#f4f8fe',
                    border: '1px solid #d0e2ff',
                    borderRadius: 1.5,
                    p: 1.5,
                    mb: 2
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <InsertDriveFileIcon sx={{ color: '#488dff', fontSize: 20 }} />
                        <Typography variant="body2" sx={{ color: '#1e3a8a', fontWeight: 500 }}>
                            Need the template format? Download sample file (.xlsx / .csv)
                        </Typography>
                    </Box>
                    <Box>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<FileDownloadIcon />}
                            endIcon={<ArrowDropDownIcon />}
                            onClick={(e) => setTemplateAnchorEl(e.currentTarget)}
                            sx={{ textTransform: 'none', backgroundColor: '#fff', fontSize: '0.82rem', borderColor: '#93c5fd' }}
                        >
                            Download Template
                        </Button>
                        <Menu
                            anchorEl={templateAnchorEl}
                            open={Boolean(templateAnchorEl)}
                            onClose={() => setTemplateAnchorEl(null)}
                        >
                            <MenuItem onClick={() => { downloadHolidayExcelTemplate(); setTemplateAnchorEl(null); }}>
                                Download Excel Template (.xlsx)
                            </MenuItem>
                            <MenuItem onClick={() => { downloadHolidayCsvTemplate(); setTemplateAnchorEl(null); }}>
                                Download CSV Template (.csv)
                            </MenuItem>
                        </Menu>
                    </Box>
                </Box>

                {/* Upload Box */}
                {!selectedFile ? (
                    <Box
                        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                        onDragLeave={() => setIsDragging(false)}
                        onDrop={handleDrop}
                        onClick={() => fileInputRef.current?.click()}
                        sx={{
                            border: `2px dashed ${isDragging ? '#488dff' : '#cbd5e1'}`,
                            borderRadius: 2,
                            backgroundColor: isDragging ? '#eff6ff' : '#fafafa',
                            p: 4,
                            textAlign: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                                borderColor: '#488dff',
                                backgroundColor: '#f8fafc'
                            }
                        }}
                    >
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".xlsx, .xls, .csv, .json"
                            style={{ display: 'none' }}
                            onChange={handleFileInput}
                        />
                        <CloudUploadIcon sx={{ fontSize: 48, color: '#488dff', mb: 1 }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b' }}>
                            Click to browse or drag & drop your holiday file
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                            Supported formats: <strong>Excel (.xlsx, .xls)</strong>, <strong>CSV (.csv)</strong>, and <strong>JSON (.json)</strong>
                        </Typography>
                    </Box>
                ) : (
                    <Box>
                        {/* File Details & Reset */}
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 1.5,
                            backgroundColor: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            borderRadius: 1.5,
                            mb: 2
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <InsertDriveFileIcon sx={{ color: '#488dff', fontSize: 24 }} />
                                <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                                        {selectedFile.name}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                                        {(selectedFile.size / 1024).toFixed(1)} KB • {parsedItems.length} rows found
                                    </Typography>
                                </Box>
                            </Box>
                            <Button
                                size="small"
                                variant="outlined"
                                color="inherit"
                                disabled={isImporting}
                                onClick={resetState}
                                sx={{ textTransform: 'none', fontSize: '0.8rem' }}
                            >
                                Choose Another File
                            </Button>
                        </Box>

                        {/* Summary Badges */}
                        <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} alignItems="center">
                            <Chip
                                size="small"
                                label={`Total: ${parsedItems.length}`}
                                sx={{ backgroundColor: '#f1f5f9', fontWeight: 600 }}
                            />
                            <Chip
                                size="small"
                                icon={<CheckCircleOutlineIcon sx={{ fontSize: '16px !important' }} />}
                                label={`Valid: ${validCount}`}
                                sx={{ backgroundColor: '#dcfce7', color: '#15803d', fontWeight: 600 }}
                            />
                            {invalidCount > 0 && (
                                <Chip
                                    size="small"
                                    icon={<ErrorOutlineIcon sx={{ fontSize: '16px !important' }} />}
                                    label={`Errors: ${invalidCount}`}
                                    sx={{ backgroundColor: '#fee2e2', color: '#b91c1c', fontWeight: 600 }}
                                />
                            )}
                        </Stack>

                        {invalidCount > 0 && (
                            <Alert severity="warning" sx={{ mb: 1.5, py: 0.5, fontSize: '0.82rem' }}>
                                {invalidCount} rows have missing or invalid data and will be skipped during import.
                            </Alert>
                        )}

                        {/* Preview Table */}
                        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 260, borderRadius: 1.5 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow sx={{ '& th': { backgroundColor: '#f8fafc', fontWeight: 600, color: '#475569', fontSize: '0.8rem' } }}>
                                        <TableCell width={50}>#</TableCell>
                                        <TableCell>Holiday Name</TableCell>
                                        <TableCell width={120}>Date</TableCell>
                                        <TableCell width={100}>Type</TableCell>
                                        <TableCell width={100}>State</TableCell>
                                        <TableCell width={90}>Status</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {parsedItems.map((item) => (
                                        <TableRow
                                            key={item.id}
                                            sx={{
                                                backgroundColor: item.isValid ? 'inherit' : '#fff5f5',
                                                '&:hover': { backgroundColor: item.isValid ? '#f8fafc' : '#fee2e2' }
                                            }}
                                        >
                                            <TableCell sx={{ fontSize: '0.8rem', color: '#64748b' }}>{item.id}</TableCell>
                                            <TableCell sx={{ fontSize: '0.82rem', fontWeight: 500, color: item.isValid ? '#1e293b' : '#dc2626' }}>
                                                {item.name || <span style={{ fontStyle: 'italic', color: '#94a3b8' }}>(Blank)</span>}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: '0.82rem', color: '#334155' }}>
                                                {item.date ? moment(item.date).format('DD MMM YYYY') : <span style={{ color: '#dc2626' }}>(Invalid)</span>}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={item.holidayType}
                                                    size="small"
                                                    sx={{
                                                        fontSize: '0.72rem',
                                                        height: 22,
                                                        backgroundColor: item.holidayType === 'Mandatory' ? '#fbecec' : '#fdf8e8',
                                                        color: item.holidayType === 'Mandatory' ? '#c33b3b' : '#e6a817',
                                                        border: `1px solid ${item.holidayType === 'Mandatory' ? '#c33b3b' : '#e6a817'}`
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ fontSize: '0.82rem', color: '#64748b' }}>{item.state}</TableCell>
                                            <TableCell>
                                                {item.isValid ? (
                                                    <Chip label="Ready" size="small" sx={{ fontSize: '0.7rem', height: 20, backgroundColor: '#dcfce7', color: '#16a34a' }} />
                                                ) : (
                                                    <Tooltip title={item.errorReason || 'Invalid'}>
                                                        <Chip label="Error" size="small" sx={{ fontSize: '0.7rem', height: 20, backgroundColor: '#fee2e2', color: '#dc2626' }} />
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                )}

                {/* Progress bar during import */}
                {isImporting && (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" sx={{ color: '#475569', mb: 0.5, display: 'block', fontWeight: 600 }}>
                            Importing holidays... {importProgress}%
                        </Typography>
                        <LinearProgress variant="determinate" value={importProgress} sx={{ height: 6, borderRadius: 1 }} />
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 2.5, py: 1.5, gap: 1 }}>
                <Button onClick={handleCloseDialog} disabled={isImporting} variant="outlined" sx={{ textTransform: 'none' }}>
                    Cancel
                </Button>
                <Button
                    onClick={handleImport}
                    variant="contained"
                    disabled={isImporting || isParsing || !selectedFile || validCount === 0}
                    sx={{
                        textTransform: 'none',
                        backgroundColor: '#49a563',
                        '&:hover': { backgroundColor: '#3e8e54' }
                    }}
                >
                    {isImporting ? `Importing (${importProgress}%)` : `Import ${validCount > 0 ? `${validCount} Holidays` : ''}`}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
